import { useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "../../api/client";
import { useNotification } from "../../context/NotificationContext";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
 Card,
 CardContent,
 CardDescription,
 CardHeader,
 CardTitle,
} from "../../components/ui/card";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Loader2, Lock, CheckCircle2, XCircle, ArrowLeft, Eye, EyeOff } from "lucide-react";

function ResetPassword() {
 const [searchParams] = useSearchParams();
 const token = searchParams.get("token");
 const navigate = useNavigate();
 const { login } = useAuth();
 const {
 register,
 handleSubmit,
 watch,
 formState: { errors },
 } = useForm();
 const [showPassword, setShowPassword] = useState(false);
 const [showConfirmPassword, setShowConfirmPassword] = useState(false);
 const [loading, setLoading] = useState(false);
 const [message, setMessage] = useState("");
 const [error, setError] = useState("");
 const { add } = useNotification();

 const password = watch("password");

 const onSubmit = async (data) => {
 if (data.password !== data.confirmPassword) {
 setError("Passwords do not match");
 return;
 }

 setLoading(true);
 setError("");
 setMessage("");

 try {
 const res = await api.post("/auth/reset-password", {
 token,
 password: data.password,
 });
 setMessage("Password reset successfully! Logging you in...");
 add("Password reset successfully!", "success");
 
 // Auto login
 if (res.data.token && res.data.user) {
 setTimeout(() => {
 login(res.data.token, res.data.user, navigate);
 }, 1500);
 } else {
 setTimeout(() => navigate("/login"), 2000);
 }
 } catch (err) {
 console.error(err);
 setError(err.response?.data?.message || "Failed to reset password");
 add(err.response?.data?.message || "Failed to reset password", "error");
 } finally {
 setLoading(false);
 }
 };

 if (!token) {
 return (
 <div className="min-h-screen flex items-center justify-center p-4">
 <Card className="w-full max-w-md">
 <CardContent className="text-center py-12">
 <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
 <XCircle className="w-6 h-6 text-destructive" />
 </div>
 <h3 className="text-lg font-semibold mb-2">Invalid Reset Link</h3>
 <p className="text-muted-foreground mb-6">
 The reset link is invalid or has expired. Please request a new one.
 </p>
 <Button asChild>
 <Link to="/forgot-password">Request New Link</Link>
 </Button>
 </CardContent>
 </Card>
 </div>
 );
 }

 return (
 <div className="min-h-screen flex items-center justify-center p-4">
 <div className="w-full max-w-md">
 <Card className="shadow-lg">
 <CardHeader className="space-y-1">
 <div className="flex items-center gap-2 mb-2">
 <div className="p-2 rounded-lg bg-primary/10">
 <Lock className="w-5 h-5 text-primary" />
 </div>
 <CardTitle className="text-2xl font-bold">
 Reset Password
 </CardTitle>
 </div>
 <CardDescription>
 Enter your new password below
 </CardDescription>
 </CardHeader>
 <CardContent>
 {message && (
 <Alert className="mb-4 border-green-500/50 bg-green-500/100/10">
 <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
 <AlertDescription className="text-green-600 dark:text-green-400">
 {message}
 </AlertDescription>
 </Alert>
 )}
 {error && (
 <Alert variant="destructive" className="mb-4">
 <XCircle className="h-4 w-4" />
 <AlertDescription>{error}</AlertDescription>
 </Alert>
 )}

 <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
 <div className="space-y-2">
 <Label htmlFor="password">New Password</Label>
 <div className="relative">
 <Input
 id="password"
 type={showPassword ? "text" : "password"}
 placeholder="Enter new password"
 {...register("password", {
 required: "Password is required",
 minLength: {
 value: 8,
 message: "Password must be at least 8 characters",
 },
 })}
 className="pr-10"
 />
 <button
 type="button"
 onClick={() => setShowPassword(!showPassword)}
 className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
 tabIndex={-1}
 >
 {showPassword ? (
 <Eye className="h-4 w-4" />
 ) : (
 <EyeOff className="h-4 w-4" />
 )}
 </button>
 </div>
 {errors.password && (
 <p className="text-sm text-destructive">
 {errors.password.message}
 </p>
 )}
 </div>

 <div className="space-y-2">
 <Label htmlFor="confirmPassword">Confirm Password</Label>
 <Input
 id="confirmPassword"
 type="password"
 placeholder="Confirm new password"
 {...register("confirmPassword", {
 required: "Please confirm your password",
 validate: value =>
 value === password || "Passwords do not match"
 })}
 />
 {errors.confirmPassword && (
 <p className="text-sm text-destructive">
 {errors.confirmPassword.message}
 </p>
 )}
 </div>

 <Button
 type="submit"
 className="w-full"
 disabled={loading}
 >
 {loading ? (
 <>
 <Loader2 className="mr-2 h-4 w-4 animate-spin" />
 Resetting...
 </>
 ) : (
 <>
 <Lock className="mr-2 h-4 w-4" />
 Reset Password
 </>
 )}
 </Button>

 <div className="text-center text-sm">
 <Link to="/login" className="text-primary hover:underline">
 Back to login
 </Link>
 </div>
 </form>
 </CardContent>
 </Card>
 </div>
 </div>
 );
}

export default ResetPassword;
